import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import ImageZoom from './ImageZoom';
import { resolveMediaUrl } from '../lib/mediaUrl';

const clamp = (min, val, max) => Math.max(min, Math.min(val, max));
const PAGE_SIZE = 6;

const GallerySlider = ({ images = [], className = '' }) => {
  const [page, setPage] = useState(0);
  const [phase, setPhase] = useState('idle'); // 'idle' | 'out' | 'in'
  const touchStartX = useRef(null);
  const animOutMs = 180;
  const animInMs = 260;

  // Normalize to objects { url, year }
  const normalized = useMemo(
    () =>
      images.map((it) =>
        typeof it === 'string'
          ? { url: it, year: '—' }
          : { url: it.url, year: it.year ?? '—' }
      ),
    [images]
  );

  // Sort by year (numeric desc first), then keep insertion for non-numeric
  const sorted = useMemo(() => {
    const yearNum = (y) => {
      const n = Number(String(y).slice(0, 4));
      return Number.isFinite(n) ? n : null;
    };
    return [...normalized].sort((a, b) => {
      const na = yearNum(a.year);
      const nb = yearNum(b.year);
      if (na != null && nb != null) return nb - na;
      if (na != null) return -1;
      if (nb != null) return 1;
      return 0;
    });
  }, [normalized]);

  // Chunk into pages of 6
  const pages = useMemo(() => {
    const out = [];
    for (let i = 0; i < sorted.length; i += PAGE_SIZE) {
      out.push(sorted.slice(i, i + PAGE_SIZE));
    }
    return out;
  }, [sorted]);

  const totalPages = pages.length || 1;

  // Clamp page when data size changes
  useEffect(() => { setPage((p) => clamp(0, p, totalPages - 1)); }, [totalPages]);

  const smoothGoto = (target) => {
    const next = clamp(0, target, totalPages - 1);
    if (next === page || phase === 'out') return;
    setPhase('out');
    setTimeout(() => {
      setPage(next);
      setPhase('in');
      setTimeout(() => setPhase('idle'), animInMs);
    }, animOutMs);
  };

  const prev = () => smoothGoto(page - 1);
  const next = () => smoothGoto(page + 1);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'ArrowLeft') prev(); if (e.key === 'ArrowRight') next(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [page, totalPages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Touch swipe
  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next() : prev());
    touchStartX.current = null;
  };

  const pageImages = pages[page] || [];

  // Year label for the top bar
  const yearLabel = useMemo(() => {
    const yrs = Array.from(new Set(pageImages.map((p) => p.year || '—'))).filter(Boolean);
    if (yrs.length === 0) return '';
    if (yrs.length === 1) return yrs[0];

    // compact range if numeric years exist
    const nums = yrs
      .map((y) => Number(String(y).slice(0, 4)))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => b - a);

    return nums.length
      ? `${nums[0]}–${nums[nums.length - 1]}`
      : 'Verschiedene Jahre';
  }, [pageImages]);

  const toSrc = (img) => resolveMediaUrl(img.url);

  if (normalized.length === 0) return null;

  return (
    <div className={`gallery-slider ${className}`} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      {/* Top bar */}
      <div className="gs-bar d-flex justify-content-between align-items-center mb-3">
        <div className="gs-count">
          {yearLabel && <span className="inner-title">{yearLabel}</span>}
        </div>
        <div className="gs-controls">
          <button
            className="gs-pill"
            onClick={prev}
            disabled={page <= 0 || phase === 'out'}
            aria-label="Vorherige Seite"
            title="Vorherige Seite"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18L9 12L15 6" />
            </svg>
          </button>
          <button
            className="gs-pill"
            onClick={next}
            disabled={page >= totalPages - 1 || phase === 'out'}
            aria-label="Nächste Seite"
            title="Nächste Seite"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 6L15 12L9 18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Grid: exactly 6 per page (last page may have fewer) */}
      <div className={`row gs-grid ${phase === 'out' ? 'anim-out' : phase === 'in' ? 'anim-in' : ''}`}>
        {pageImages.map((img, i) => (
          <div key={`${img.url}-${i}`} className="col-md-6 col-sm-6 col-lg-4 galleryBoxCentered">
            <ImageZoom imageUrl={toSrc(img)} />
          </div>
        ))}
      </div>

      {/* Dots per page */}
      <div className="gs-dots mt-1">
        {Array.from({ length: totalPages }).map((_, i) => (
          <button
            key={i}
            className={`gs-dot ${i === page ? 'is-active' : ''}`}
            onClick={() => smoothGoto(i)}
            aria-label={`Seite ${i + 1}`}
            title={`Seite ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default GallerySlider;
